/**
 * Rating-schema client helpers for `@proof/client-core`.
 *
 * Provides typed wrappers over the oRPC `ratingSchema.*` procedures so the
 * rating-schema editor UI (Track 2.H) and any other consumer can call them
 * without knowing the raw oRPC procedure paths.
 *
 * Spec refs:
 *   functionality.md §6.3 — rating dimensions, types, versioning contract
 *   extensibility-design.md §3.8 — user-declared composite rating types
 *   architecture.md §4.7 — rating_schema_versions DDL
 *
 * Versioning contract (functionality.md §6.3 lines 1488–1495):
 *   - Each schema edit inserts a new `rating_schema_versions` row.
 *   - The recipe's convenience pointer (`rating_schema_json`) moves to the new
 *     schema content.
 *   - Historical `attempts.rating_schema_version_uuid` pointers are NEVER
 *     changed — old attempts keep their schema version.
 */

import type { ApiClient } from "./index.js";

// ── Public domain types ────────────────────────────────────────────────────────

/**
 * Builtin rating dimension types (functionality.md §6.3 lines 1499–1511).
 */
export type BuiltinDimensionType =
  | "stars"
  | "boolean"
  | "enum"
  | "number"
  | "text"
  | "coord"
  | "duration"
  | "photo_ref"
  | "artefact_ref";

/**
 * Valid scope values for a rating dimension.
 *
 * - `"recipe"`  — dimension applies to the whole recipe (default, broadest scope).
 * - `"section"` — scoped to a named `@section` in the recipe body. Wave 3's
 *   per-section rating sheet (A.4.4) filters dimensions by matching scope to
 *   the section currently being rated.
 * - `"attempt"` — applies per individual attempt/cook.
 *
 * Omitting the field is equivalent to `"recipe"` — backward-compatible with
 * all pre-010 schema versions.
 *
 * Gap doc ref: redesign-functionality-gap.md §2.6 A.6.5
 */
export type RatingDimensionScope = "recipe" | "section" | "attempt";

/**
 * A single rating dimension as it appears in a recipe's schema.
 *
 * The `type` field accepts both builtin string literals and user-declared
 * composite type names from `.cookrc.yml` (extensibility-design.md §3.8).
 */
export interface RatingDimension {
  name: string;
  /**
   * Builtin type literal OR user-declared composite type name. When the type
   * name is not one of the builtins it is treated as a composite-candidate;
   * the cookrc validator (Track 2.C) confirms or rejects it at save time.
   */
  type: BuiltinDimensionType | string;
  /** `stars` only — denominator. Default 5. */
  max?: number;
  /** `enum` only — ordered option list. Required when type === "enum". */
  options?: string[];
  /** `number` only — optional unit label, e.g. "°F". */
  unit?: string;
  /** Optional dimension description. */
  description?: string;
  /**
   * Whether this dimension contributes to the computed overall score.
   * Only numeric/stars dimensions contribute by default.
   */
  contributesToOverall?: boolean;
  /**
   * The scope at which this dimension is rated.
   *
   * - `"recipe"` (default when omitted) — applies to the recipe as a whole.
   * - `"section"` — scoped to a named `@section` in the recipe body. Wave 3's
   *   per-section rating sheet (A.4.4) filters dimensions whose scope matches
   *   the section being rated (e.g. `dim.scope === "section"`).
   * - `"attempt"` — applies per individual attempt/cook.
   *
   * Field is optional; absent = `"recipe"` for backward compatibility with
   * all pre-010 schema versions.
   *
   * Gap doc ref: redesign-functionality-gap.md §2.6 A.6.5
   */
  scope?: RatingDimensionScope;
}

/**
 * The parsed content of `rating_schema_versions.schema_json`.
 *
 * `ratingTypes` is the forwarded `.cookrc.yml` `rating_types:` block for
 * composite-type rendering without a separate cookrc fetch.
 * (extensibility-design.md §3.8)
 */
export interface RatingSchemaContent {
  dimensions: RatingDimension[];
  overall?: "average" | "weighted_average" | "user_entered" | null;
  /**
   * User-declared composite types.
   * Shape: { typeName: { shape: { fieldName: innerType } } }
   */
  ratingTypes?: Record<string, { shape: Record<string, string> }>;
}

/**
 * A rating schema version row returned from the API.
 * Maps directly to `rating_schema_versions` DDL (architecture.md §4.7).
 */
export interface RatingSchemaVersionRecord {
  uuid: string;
  recipeUuid: string;
  /** JSON-stringified `RatingSchemaContent`. Parse with `parseSchemaJson`. */
  schemaJson: string;
  /** UUID of the version this supersedes (null for the first version). */
  supersedesUuid: string | null;
  authorUuid: string;
  createdAt: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Safely parse the `schemaJson` column into a `RatingSchemaContent`.
 * Returns `null` if the JSON is malformed or the shape does not match.
 */
export function parseSchemaJson(schemaJson: string): RatingSchemaContent | null {
  try {
    const parsed = JSON.parse(schemaJson) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !Array.isArray((parsed as { dimensions?: unknown }).dimensions)
    ) {
      return null;
    }
    return parsed as RatingSchemaContent;
  } catch {
    return null;
  }
}

/**
 * Serialize a `RatingSchemaContent` to a JSON string suitable for the API.
 */
export function serializeSchemaContent(content: RatingSchemaContent): string {
  return JSON.stringify(content);
}

/**
 * Returns true if `type` is one of the nine shipped builtin types.
 * False means the type is user-declared (composite candidate from .cookrc.yml).
 */
export function isBuiltinType(type: string): type is BuiltinDimensionType {
  const builtins: readonly string[] = [
    "stars",
    "boolean",
    "enum",
    "number",
    "text",
    "coord",
    "duration",
    "photo_ref",
    "artefact_ref",
  ];
  return builtins.includes(type);
}

// ── API client wrappers ───────────────────────────────────────────────────────

/**
 * Create a new rating schema version for a recipe.
 *
 * The server:
 *   - Validates the schema JSON structure + dimension types.
 *   - Inserts a new `rating_schema_versions` row pointing at the previous
 *     version via `supersedes_uuid`.
 *   - Updates `recipes.rating_schema_json` to the new schema content.
 *   - Leaves all `attempts.rating_schema_version_uuid` pointers unchanged.
 *   - Server-stamps `author_agent` from the session (never from input).
 *
 * @param api        - Typed API client from `createApiClient`.
 * @param recipeUuid - The recipe UUID whose schema is being updated.
 * @param content    - The new schema content (will be JSON-stringified).
 */
export async function createRatingSchemaVersion(
  api: ApiClient,
  recipeUuid: string,
  content: RatingSchemaContent,
): Promise<unknown> {
  return api.ratingSchema.create({
    recipeUuid,
    schemaJson: serializeSchemaContent(content),
  });
}

/**
 * Get a specific rating schema version by its UUID.
 *
 * Used when an attempt's `rating_schema_version_uuid` points at a historical
 * version that differs from the recipe's current schema.
 *
 * @param api         - Typed API client from `createApiClient`.
 * @param versionUuid - The UUID of the specific version row.
 */
export async function getRatingSchemaVersion(
  api: ApiClient,
  versionUuid: string,
): Promise<unknown> {
  return api.ratingSchema.get({ uuid: versionUuid });
}

/**
 * List all rating schema versions for a recipe, oldest-first.
 *
 * The oldest-first ordering matches the supersedes chain direction: each row
 * N+1 has `supersedes_uuid = row[N].uuid`. The last entry is the current
 * schema (matching `recipes.rating_schema_json`).
 *
 * @param api        - Typed API client from `createApiClient`.
 * @param recipeUuid - The recipe UUID.
 */
export async function listRatingSchemaVersions(
  api: ApiClient,
  recipeUuid: string,
): Promise<unknown> {
  return api.ratingSchema.list({ recipeUuid });
}
